/* eslint-disable complexity */

import React, { useCallback } from 'react';
import Textbox from 'carbon-react/lib/components/textbox';

import { ChartOfAccountsAccount } from '@futrli-sdk/predict';
import { useIntl } from '@futrli/localisation';
import { UICarbonNumberField } from '@futrli/ui-components';

import { ConsolidationEntity } from '../../../../hooks/useConsolidationEntities';
import { formatTAXName } from '../../../../core/formatters';
import { MAX_PERCENTAGE } from '../../../../core/constants';
import arrow from './arrow.svg';
import * as styles from './basic-info.scss';
import { SubEntityAutocomplete } from './sub-entity-autocomplete';
import { AccountAutocomplete } from './account-autocomplete';

interface Props {
  predictionName: string;
  accountIndex: string;
  entities?: ConsolidationEntity[];
  entity?: ConsolidationEntity;
  entityFieldDisabled?: boolean;
  handlePredictionNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleVatPercentageChange: (value: string) => void;
  handleAccountIndexAutocompleteChange: (value: ChartOfAccountsAccount | null) => void;
  handleEntityAutocompleteChange?: (value: ConsolidationEntity | null) => void;
  // journalDefaults: string;
  // handleJournalDefaultsChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  // journalsCurrentAccount: string;
  // handleJournalsCurrentAccountChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  accountRows?: ChartOfAccountsAccount[];
  vatPercentage?: string;
  currency?: string | null;
  defaultVatRate?: number;
  vatDisabled?: boolean;
  predictionContentTouched: boolean;
}

export const BasicInfo = ({
  predictionName,
  accountIndex,
  handlePredictionNameChange,
  handleVatPercentageChange,
  handleAccountIndexAutocompleteChange,
  handleEntityAutocompleteChange,
  accountRows = [],
  entities,
  entity,
  entityFieldDisabled = false,
  vatPercentage,
  currency,
  defaultVatRate = 0,
  vatDisabled,
  predictionContentTouched
}: Props) => {
  const intl = useIntl();
  const entityNotSelected = entities && !entity;

  const handleAccountChangeCarbon = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const accountIndex = event.target.value;
      const newSelectedAccount = accountRows.find(o => o.code === accountIndex);
      handleAccountIndexAutocompleteChange(newSelectedAccount || null);
    },
    [accountRows, handleAccountIndexAutocompleteChange]
  );

  const handleSubEntityChangeCarbon = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const entityId = event.target.value;
      const newSelectedEntity = entities?.find(e => e.id === entityId);
      handleEntityAutocompleteChange?.(newSelectedEntity || null);
    },
    [entities, handleEntityAutocompleteChange]
  );

  return (
    <div className={styles.basicInfo}>
      <div style={{ width: 320 }}>
        <Textbox
          label={intl.formatMessage({
            id: 'predict.enter_prediction_name',
            defaultMessage: 'Enter prediction name'
          })}
          placeholder={intl.formatMessage({
            id: 'predict.make_it_memorable',
            defaultMessage: 'Make it memorable...'
          })}
          value={predictionName}
          onChange={handlePredictionNameChange}
          name="Prediction name"
          mr="8px"
          error={predictionContentTouched && !predictionName.length}
          size="large"
          mb={0}
        />
      </div>

      {entities && (
        <>
          <img src={arrow} className={styles.arrowCarbon} alt="arrow" aria-hidden />
          <SubEntityAutocomplete
            entityFieldDisabled={entityFieldDisabled}
            entity={entity}
            entities={entities}
            predictionContentTouched={predictionContentTouched}
            handleSubEntityChangeCarbon={handleSubEntityChangeCarbon}
          />
        </>
      )}

      <img src={arrow} className={styles.arrowCarbon} alt="arrow" aria-hidden />
      <AccountAutocomplete
        accountIndex={accountIndex}
        accountRows={accountRows}
        entityNotSelected={entityNotSelected}
        predictionContentTouched={predictionContentTouched}
        handleAccountChangeCarbon={handleAccountChangeCarbon}
      />
      {vatDisabled ? null : (
        <>
          <img src={arrow} className={styles.arrowCarbon} alt="arrow" aria-hidden />
          <UICarbonNumberField
            autoComplete="off"
            clampMax={MAX_PERCENTAGE}
            clampMin={0}
            onChange={handleVatPercentageChange}
            placeholder={intl.formatMessage(
              { id: 'predict.value_percent_default', defaultMessage: '{defaultVatRate}% default' },
              { defaultVatRate }
            )}
            type="percentage"
            disabled={entityNotSelected}
            value={vatPercentage}
            label={intl.formatMessage(
              {
                id: 'predict.tax_name_rate',
                defaultMessage: `{taxName} rate`
              },
              { taxName: formatTAXName(currency || '', intl) }
            )}
            style={{ width: 160 }}
            size="large"
            mb={0}
          />
        </>
      )}
      {/* <UITextField
          label={intl.formatMessage({
            id: 'predict.predictions.journal_defaults',
            defaultMessage: 'Journal defaults'
          })}
          required={true}
          value={journalDefaults}
          onChange={handleJournalDefaultsChange}
          name="Journal defaults"
          classes={{
            root: styles.basicInfoInput
          }}
          InputLabelProps={{
            classes: { root: classNames(styles.label, styles.labelSpace) }
          }}
          style={{ marginRight: 8, marginLeft: 80 }}
          select={true}
        >
          <UIMenuItem value="debit" variant="forSelect">
            Debit (default)
          </UIMenuItem>
        </UITextField>
        <UITextField
          required={true}
          value={accountName}
          onChange={handleAccountNameChange}
          classes={{
            root: styles.basicInfoInput
          }}
          InputLabelProps={{
            classes: { root: styles.label }
          }}
          style={{ marginRight: 8, marginTop: 24 }}
          select={true}
        >
          {accountRows
            .filter(row => typeof get(row, 'parentName') === 'undefined')
            .map(row => (
              <UIMenuItem key={row.account.name} value={row.account.name} variant="forSelect">
                {row.account.name}
              </UIMenuItem>
            ))}
        </UITextField> */}
    </div>
  );
};
